/**
 * The application shell: the dark green top navigation and the page frame.
 *
 * One shell, two navigation sets. The staff bar stacks an icon above its label
 * across ten destinations and carries global search; the family bar puts the
 * icon beside the label across seven and has no search, because a parent with
 * two children searching for them is noise rather than help.
 *
 * Navigation is filtered by permission before it renders. That is a
 * convenience — the server refuses the route either way — but a menu item that
 * leads to a 403 is the kind of dead end the brief rules out.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { photoTransformCss, type Action, type Module, type PhotoTransform } from '@cornerstone/education-domain'
import { useSession } from '@/lib/session'
import { SchoolMarkCompact } from './SchoolMark'
import { TenantSwitcher } from './TenantSwitcher'
import { BackLink } from './BackLink'
import { ViewSwitcher } from './ViewSwitcher'
import { FeedbackPanel } from '@/features/feedback/FeedbackPanel'
import {
  IconAcademics,
  IconAdministration,
  IconAssets,
  IconDocument,
  IconClock,
  IconAssignments,
  IconAttendance,
  IconBell,
  IconChevronDown,
  IconFinance,
  IconGrades,
  IconHome,
  IconLogout,
  IconSettings,
  IconUser,
  IconMessages,
  IconOperations,
  IconFacilities,
  IconTransportation,
  IconPurchasing,
  IconReports,
  IconResources,
  IconScheduling,
  IconStudents,
  IconHealth,
} from './icons'
import { CommandSearch } from './CommandSearch'
import { OccasionGreeting } from './OccasionGreeting'
import { UpdateNotice } from './UpdateNotice'
import { request } from '@/lib/api'
import type { UserPreferences } from '@/lib/preferences'
import './shell.css'

interface NavItem {
  to: string
  label: string
  icon: typeof IconHome
  /** Hidden unless the role grants this. Omitted means always visible. */
  requires?: { module: Module; action: Action }
  /** Family nav: shown to guardians, hidden from students. */
  guardianOnly?: boolean
  substituteOnly?: boolean
  /** Hidden from these roles even when the module is granted (they reach it elsewhere). */
  hiddenFor?: string[]
  /** Hidden until a linked student is in grade 9 or above. */
  highSchoolOnly?: boolean
  /** Family nav: shown to students, hidden from guardians. */
  studentOnly?: boolean
}

/**
 * A heading in the bar with destinations beneath it.
 *
 * Thirty destinations in one flat bar had stopped being navigation. These are
 * the working categories; every destination keeps its own permission gate, so
 * a group shows a role only the parts it may open and disappears entirely
 * when none remain. A group left with a single visible item renders as that
 * item directly — a menu of one is a click tax.
 */
interface NavGroup {
  label: string
  icon: typeof IconHome
  children: NavItem[]
}

type NavEntry = NavItem | NavGroup

const STAFF_NAV: NavEntry[] = [
  { to: '/', label: 'Home', icon: IconHome },
  { to: '/work', label: 'My Work', icon: IconAssignments },
  { to: '/calendar', label: 'Calendar', icon: IconClock },
  { to: '/sub-today', label: 'Today’s Assignment', icon: IconClock, substituteOnly: true },
  {
    label: 'Students',
    icon: IconStudents,
    children: [
      { to: '/students', label: 'Students', icon: IconStudents, requires: { module: 'students', action: 'view' } },
      { to: '/admissions', label: 'Admissions', icon: IconDocument, requires: { module: 'admissions', action: 'view' } },
      { to: '/forms', label: 'Forms', icon: IconDocument, requires: { module: 'forms', action: 'view' } },
      { to: '/graduation', label: 'Graduation', icon: IconAcademics, requires: { module: 'graduation', action: 'view' } },
      { to: '/my-class', label: 'My Class', icon: IconAcademics, requires: { module: 'assignments', action: 'view' } },
      { to: '/counseling', label: 'Counseling', icon: IconStudents, requires: { module: 'counseling', action: 'view' } },
      { to: '/special-programs', label: 'Special Programs', icon: IconResources, requires: { module: 'iep', action: 'view' } },
      { to: '/discipline', label: 'Discipline', icon: IconStudents, requires: { module: 'discipline', action: 'create' } },
      { to: '/health-office', label: 'Health Office', icon: IconHealth, requires: { module: 'medical', action: 'view' } },
      { to: '/activities', label: 'Activities', icon: IconClock, requires: { module: 'activities', action: 'view' } },
    ],
  },
  {
    label: 'Academics',
    icon: IconAcademics,
    children: [
      // The catalog rebuilt in the scheduling pass replaces the old section
      // list as the front door. `/academics` stays routed for existing links.
      { to: '/courses', label: 'Course Catalog', icon: IconAcademics, requires: { module: 'courses', action: 'view' } },
      { to: '/grades', label: 'Grades', icon: IconGrades, requires: { module: 'grades', action: 'view' } },
      { to: '/attendance', label: 'Attendance', icon: IconAttendance, requires: { module: 'attendance', action: 'view' } },
      { to: '/assessments', label: 'Assessments', icon: IconGrades, requires: { module: 'assessments', action: 'view' } },
      { to: '/curriculum', label: 'Curriculum', icon: IconAcademics, requires: { module: 'curriculum', action: 'view' } },
      { to: '/scheduling', label: 'Scheduling', icon: IconScheduling, requires: { module: 'schedules', action: 'view' } },
      { to: '/scheduling/runs', label: 'Scheduling Runs', icon: IconScheduling, requires: { module: 'masterSchedule', action: 'view' } },
    ],
  },
  {
    label: 'Finance',
    icon: IconFinance,
    children: [
      // The financial operating system. First in the group because the
      // others are its tributaries: student fees, purchase requests and meal
      // takings all end up posting here.
      { to: '/finance', label: 'Finance', icon: IconFinance, requires: { module: 'finance', action: 'view' } },
      // The student fee ledger (Fees functional pass). Finance is the district ledger it posts to.
      { to: '/fees', label: 'Fees', icon: IconReports, requires: { module: 'fees', action: 'view' } },
      { to: '/purchasing', label: 'Purchasing', icon: IconPurchasing, requires: { module: 'purchasing', action: 'view' } },
      { to: '/cafeteria', label: 'Cafeteria', icon: IconReports, requires: { module: 'cafeteria', action: 'view' } },
    ],
  },
  {
    label: 'Staff',
    icon: IconStudents,
    children: [
      { to: '/staff', label: 'Staff', icon: IconStudents, requires: { module: 'staff', action: 'view' } },
      { to: '/absences', label: 'Absences', icon: IconClock, requires: { module: 'absences', action: 'view' } },
      { to: '/evaluations', label: 'Evaluations', icon: IconReports, requires: { module: 'evaluations', action: 'view' } },
    ],
  },
  {
    label: 'Operations',
    icon: IconOperations,
    children: [
      { to: '/operations', label: 'Operations', icon: IconOperations, requires: { module: 'operations', action: 'view' } },
      { to: '/transportation', label: 'Transportation', icon: IconTransportation, requires: { module: 'transportation', action: 'view' }, hiddenFor: ['teacher', 'substitute_teacher'] },
      { to: '/dismissal', label: 'Dismissal', icon: IconClock, requires: { module: 'dismissal', action: 'view' } },
      { to: '/emergency', label: 'Emergency', icon: IconHealth, requires: { module: 'emergency', action: 'view' } },
      { to: '/visitors', label: 'Visitors', icon: IconStudents, requires: { module: 'visitors', action: 'view' } },
      { to: '/facilities', label: 'Facilities', icon: IconFacilities, requires: { module: 'facilities', action: 'view' } },
      { to: '/library', label: 'Library', icon: IconDocument, requires: { module: 'library', action: 'view' } },
      { to: '/assets', label: 'Assets', icon: IconAssets, requires: { module: 'assets', action: 'view' } },
    ],
  },
  { to: '/messages', label: 'Messages', icon: IconMessages, requires: { module: 'messages', action: 'view' } },
  { to: '/documents', label: 'Documents', icon: IconDocument, requires: { module: 'documents', action: 'view' } },
  {
    label: 'Reports',
    icon: IconReports,
    children: [
      { to: '/reports', label: 'Reports', icon: IconReports, requires: { module: 'reports', action: 'view' } },
      { to: '/state-reporting', label: 'State Reporting', icon: IconReports, requires: { module: 'stateReporting', action: 'view' } },
      { to: '/print-center', label: 'Print Center', icon: IconDocument, requires: { module: 'printCenter', action: 'view' } },
    ],
  },
  { to: '/administration', label: 'Administration', icon: IconAdministration, requires: { module: 'schools', action: 'view' } },
]

/**
 * What a platform operator sees in their own tenant.
 *
 * One destination, because there is only one thing to do from here. The
 * operator tenant holds no school, so the staff navigation above pointed every
 * item at an empty tenant — nine links to nine empty states, and a Home that
 * errored outright. The brief rules out menu items that lead nowhere, and a
 * link to a page with nothing on it is the same dead end whether it fails or
 * renders blank.
 */
const PLATFORM_NAV: NavItem[] = [
  { to: '/', label: 'Platform', icon: IconHome },
  { to: '/administration/focus-group', label: 'Focus Group', icon: IconStudents, requires: { module: 'focusGroup', action: 'view' } },
  { to: '/administration/feedback', label: 'Feedback', icon: IconMessages, requires: { module: 'feedbackAdmin', action: 'view' } },
]

/**
 * What a focus group tester sees.
 *
 * The school navigation, because touring the product is the job, plus the Lab
 * itself. Administration is absent: a tester holds none of those modules, so
 * the filter would drop it anyway, and listing it would be a menu item that
 * resolves to a refusal.
 */
const TESTER_NAV: NavItem[] = [
  { to: '/feedback', label: 'Feedback Lab', icon: IconMessages },
  { to: '/', label: 'Dashboard', icon: IconHome },
  { to: '/work', label: 'My Work', icon: IconAssignments, requires: { module: 'sections', action: 'view' } },
  { to: '/students', label: 'Students', icon: IconStudents, requires: { module: 'students', action: 'view' } },
  { to: '/academics', label: 'Academics', icon: IconAcademics, requires: { module: 'courses', action: 'view' } },
  { to: '/attendance', label: 'Attendance', icon: IconAttendance, requires: { module: 'attendance', action: 'view' } },
  { to: '/grades', label: 'Grades', icon: IconGrades, requires: { module: 'grades', action: 'view' } },
  { to: '/reports', label: 'Reports', icon: IconReports, requires: { module: 'reports', action: 'view' } },
]

const FAMILY_NAV: NavItem[] = [
  { to: '/', label: 'Home', icon: IconHome },
  { to: '/grades', label: 'Grades', icon: IconGrades },
  { to: '/assignments', label: 'Assignments', icon: IconAssignments },
  { to: '/attendance', label: 'Attendance', icon: IconAttendance },
  { to: '/schedule', label: 'Schedule', icon: IconClock },
  { to: '/messages', label: 'Messages', icon: IconMessages },
  { to: '/calendar', label: 'Calendar', icon: IconClock },
  { to: '/forms', label: 'Forms', icon: IconDocument },
  { to: '/fees', label: 'Fees', icon: IconReports },
  { to: '/transportation', label: 'Transportation', icon: IconTransportation },
  { to: '/activities', label: 'Activities', icon: IconClock },
  { to: '/cafeteria', label: 'Meals', icon: IconReports },
  { to: '/documents', label: 'Documents', icon: IconDocument },
  { to: '/health', label: 'Health', icon: IconHealth },
  { to: '/course-requests', label: 'Course Requests', icon: IconAcademics, studentOnly: true, highSchoolOnly: true },
  { to: '/graduation', label: 'Graduation', icon: IconAcademics, highSchoolOnly: true },
  { to: '/college-career', label: 'College & Career', icon: IconAcademics, highSchoolOnly: true },
  { to: '/apply', label: 'Admissions', icon: IconDocument, guardianOnly: true },
]

/**
 * The navigation a person gets: their variant's list, cut to what their role
 * may open. Shared with Preferences so the start-page choices are exactly the
 * pages in the person's own menu, never one their permissions would refuse.
 */
export function navEntriesFor({
  variant,
  can,
  role,
  isPlatformConsole,
  hasHighSchooler = true,
  familyChildren = [],
}: {
  variant: 'staff' | 'family'
  can: (module: Module, action: Action) => boolean
  role: string
  isPlatformConsole: boolean
  hasHighSchooler?: boolean
  familyChildren?: { id: string; displayName: string }[]
}): NavEntry[] {
  // A tester is identified by holding the Lab, which only they and the
  // operators do — and an operator in the console is caught by the branch
  // above, so this cannot swallow them.
  const isTester = can('feedbackLab', 'create') && !can('focusGroup', 'view')

  const allowItem = (item: NavItem) =>
    (!item.requires || can(item.requires.module, item.requires.action)) &&
    (!item.substituteOnly || role === 'substitute_teacher') &&
    (!item.hiddenFor || !item.hiddenFor.includes(role))

  // A parent's menu: Home, then one Student entry (a menu of their
  // children, or a single link for an only child), then the household pages.
  // Everything about a child lives on that child's page.
  if (variant === 'family' && role === 'parent_guardian') {
    const household = new Set(['/messages', '/calendar', '/forms', '/fees', '/documents', '/apply'])
    const studentEntry: NavEntry[] =
      familyChildren.length === 0
        ? []
        : familyChildren.length === 1
          ? [{ to: `/student/${familyChildren[0]!.id}`, label: 'Student', icon: IconStudents }]
          : [
              {
                label: 'Student',
                icon: IconStudents,
                children: familyChildren.map((child) => ({ to: `/student/${child.id}`, label: child.displayName, icon: IconStudents })),
              },
            ]
    return [
      FAMILY_NAV[0]!,
      ...studentEntry,
      ...FAMILY_NAV.filter((item) => household.has(item.to)),
    ]
  }

  return variant === 'family'
    ? FAMILY_NAV.filter(
        (item) =>
          (!item.guardianOnly || role === 'parent_guardian') &&
          (!item.studentOnly || role === 'student') &&
          (!item.highSchoolOnly || hasHighSchooler),
      ).filter(allowItem)
    : isPlatformConsole
      ? PLATFORM_NAV.filter(allowItem)
      : isTester
        ? TESTER_NAV.filter(allowItem)
        : STAFF_NAV.map((entry) =>
            'children' in entry ? { ...entry, children: entry.children.filter(allowItem) } : entry,
          )
            .filter((entry) => ('children' in entry ? entry.children.length > 0 : allowItem(entry)))
            .map((entry): NavEntry => ('children' in entry && entry.children.length === 1 && entry.children[0] ? entry.children[0] : entry))
}

/** Every page in a person's menu, flattened, for choosing a start page. */
export function destinationsFor(entries: NavEntry[]): { to: string; label: string }[] {
  const seen = new Set<string>()
  const out: { to: string; label: string }[] = []
  for (const entry of entries) {
    for (const item of 'children' in entry ? entry.children : [entry]) {
      if (seen.has(item.to)) continue
      seen.add(item.to)
      out.push({ to: item.to, label: item.label })
    }
  }
  return out
}

export function AppShell({ variant, children }: { variant: 'staff' | 'family'; children: ReactNode }) {
  const { session, can, signOut, isPlatformConsole } = useSession()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const [openGroup, setOpenGroup] = useState<string | null>(null)
  // Desktop menus open on hover; touch screens and the narrow layout keep
  // tap-to-open, where hover does not exist.
  const closeTimer = useRef<number | null>(null)
  const canHover = () =>
    typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 1025px)').matches
  const hoverOpen = (label: string) => {
    if (!canHover()) return
    if (closeTimer.current) window.clearTimeout(closeTimer.current)
    setOpenGroup(label)
  }
  // A short grace period, so crossing a gap or brushing the edge on the way
  // into the panel does not snap it shut.
  const hoverClose = () => {
    if (!canHover()) return
    if (closeTimer.current) window.clearTimeout(closeTimer.current)
    closeTimer.current = window.setTimeout(() => setOpenGroup(null), 180)
  }
  const navRef = useRef<HTMLElement>(null)
  // myEdu hides high-school modules until a linked student is in grade 9+.
  const [hasHighSchooler, setHasHighSchooler] = useState(true)
  const [familyChildren, setFamilyChildren] = useState<{ id: string; displayName: string }[]>([])
  useEffect(() => {
    if (variant !== 'family') return
    let cancelled = false
    import('@/lib/api').then(({ api }) =>
      api
        .familyStudents()
        .then((result) => {
          if (cancelled) return
          setFamilyChildren(result.items as { id: string; displayName: string }[])
          const grades = (result.items as { gradeLevel?: string }[]).map((child) => {
            const raw = (child.gradeLevel ?? '').toUpperCase()
            if (raw === 'K' || raw === 'PK') return 0
            const parsed = Number.parseInt(raw, 10)
            return Number.isFinite(parsed) ? parsed : 0
          })
          setHasHighSchooler(grades.length === 0 || grades.some((grade) => grade >= 9))
        })
        .catch(() => {}),
    )
    return () => {
      cancelled = true
    }
  }, [variant])

  // Close both menus on navigation, or a tap-through on a phone leaves the
  // menu covering the page it just opened.
  useEffect(() => {
    setMenuOpen(false)
    setMobileNavOpen(false)
    setOpenGroup(null)
  }, [location.pathname])

  // An open group closes on a click anywhere else, or on Escape — the same
  // contract as every other menu in the shell.
  useEffect(() => {
    if (!openGroup) return
    function onDocumentClick(event: MouseEvent) {
      if (event.target instanceof Node && navRef.current && !navRef.current.contains(event.target)) {
        setOpenGroup(null)
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpenGroup(null)
    }
    document.addEventListener('mousedown', onDocumentClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocumentClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [openGroup])

  if (!session) return null

  const entries = navEntriesFor({
    variant,
    can,
    role: session.user.role,
    isPlatformConsole,
    hasHighSchooler,
    familyChildren,
  })

  const isGroupActive = (group: NavGroup) =>
    group.children.some((item) => (item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to)))

  return (
    <div className="shell" data-variant={variant}>
      <a className="skip-link" href="#main">
        Skip to main content
      </a>

      <header className="topbar">
        <div className="topbar__brand">
          <SchoolMarkCompact className="topbar__mark" />
          <span className="topbar__name">
            <strong>Cornerstone</strong> Education
          </span>
        </div>

        <button
          type="button"
          className="topbar__burger"
          aria-expanded={mobileNavOpen}
          aria-controls="primary-nav"
          onClick={() => setMobileNavOpen((open) => !open)}
        >
          <span className="sr-only">{mobileNavOpen ? 'Close menu' : 'Open menu'}</span>
          <svg viewBox="0 0 24 24" aria-hidden="true" width="24" height="24">
            <path
              d={mobileNavOpen ? 'M6 6l12 12M18 6L6 18' : 'M4 7h16M4 12h16M4 17h16'}
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </button>

        <nav className="topnav" id="primary-nav" data-open={mobileNavOpen} aria-label="Primary" ref={navRef}>
          <ul>
            {entries.map((entry) =>
              'children' in entry ? (
                <li
                  key={entry.label}
                  className="topnav__group"
                  onMouseEnter={() => hoverOpen(entry.label)}
                  onMouseLeave={hoverClose}
                >
                  <button
                    type="button"
                    className={`topnav__group-button${isGroupActive(entry) ? ' is-active' : ''}`}
                    aria-expanded={openGroup === entry.label}
                    // With hover the menu is already open; a click must not close it.
                    onClick={() =>
                      setOpenGroup((current) => (canHover() ? entry.label : current === entry.label ? null : entry.label))
                    }
                  >
                    <entry.icon className="topnav__icon" />
                    <span>
                      {entry.label}
                      <span className="topnav__caret" aria-hidden="true">
                        {'\u25BE'}
                      </span>
                    </span>
                  </button>
                  <ul className={`topnav__menu${openGroup === entry.label ? ' is-open' : ''}`}>
                    {entry.children.map((item) => (
                      <li key={item.to}>
                        <NavLink to={item.to} className={({ isActive }) => (isActive ? 'is-active' : '')}>
                          <item.icon className="topnav__icon" />
                          <span>{item.label}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </li>
              ) : (
                <li key={entry.to}>
                  <NavLink
                    to={entry.to}
                    end={entry.to === '/'}
                    className={({ isActive }) => (isActive ? 'is-active' : '')}
                  >
                    <entry.icon className="topnav__icon" />
                    <span>{entry.label}</span>
                  </NavLink>
                </li>
              ),
            )}
          </ul>
        </nav>

        <div className="topbar__actions">
          {variant === 'staff' && !isPlatformConsole && <CommandSearch />}

          <ViewSwitcher />
          <TenantSwitcher />

          <button type="button" className="topbar__icon-button" aria-label="Notifications">
            <IconBell />
          </button>

          <div className="topbar__account">
            <button
              type="button"
              className="topbar__account-trigger"
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              onClick={() => setMenuOpen((open) => !open)}
            >
              <Avatar name={session.user.displayName} />
              <span className="topbar__account-text">
                <span className="topbar__account-name">{session.user.displayName}</span>
                <span className="topbar__account-role">{session.user.roleLabel}</span>
              </span>
              <IconChevronDown className="topbar__account-caret" />
            </button>

            {menuOpen && (
              <AccountMenu
                onClose={() => setMenuOpen(false)}
                onSignOut={() => void signOut()}
                email={session.user.email}
                tenantName={session.tenant.name}
                edition={session.entitlements.edition}
              />
            )}
          </div>
        </div>
      </header>

      <UpdateNotice />

      <main className="shell__main" id="main" tabIndex={-1}>
        <BackLink />
        {children}
      </main>

      {/* Renders for focus group testers and nobody else; it checks the
          grant itself rather than being conditionally mounted, so there is
          one place that decides. */}
      <FeedbackPanel />
      <OccasionGreeting />
      <StartPage destinations={destinationsFor(entries)} />
    </div>
  )
}

/**
 * Sends a person to their chosen start page (Default Landing Tab) once,
 * straight after signing in, or when the app is opened fresh at its root.
 *
 * Only from Home (a deep link or a reload of another page keeps its page),
 * only once per load, and only to a page in their own menu: a start page
 * saved under a role they no longer hold is ignored rather than followed
 * into a refusal, and they stay on Home.
 */
let openedThisLoad = false

function StartPage({ destinations }: { destinations: { to: string }[] }) {
  const { freshSignIn, clearFreshSignIn } = useSession()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (!freshSignIn && openedThisLoad) return
    if (freshSignIn) clearFreshSignIn()
    if (location.pathname !== '/' || location.search || location.hash) {
      openedThisLoad = true
      return
    }
    let cancelled = false
    request<{ preferences: UserPreferences }>('/me/preferences')
      .then(({ preferences }) => {
        if (cancelled) return
        openedThisLoad = true
        const target = preferences.landingPage
        if (!target || target === '/' || window.location.pathname !== '/') return
        if (destinations.some((item) => item.to === target)) navigate(target, { replace: true })
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
    // Once per sign-in; the destinations are read at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freshSignIn])

  return null
}

function AccountMenu({
  onClose,
  onSignOut,
  email,
  tenantName,
  edition,
}: {
  onClose: () => void
  onSignOut: () => void
  email: string
  tenantName: string
  edition: string
}) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      if (!ref.current?.contains(event.target as Node)) onClose()
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose])

  return (
    <div className="account-menu" ref={ref} role="menu">
      <div className="account-menu__header">
        <p className="account-menu__email">{email}</p>
        <p className="account-menu__tenant">
          {tenantName}
          <span className="pill pill--neutral">{edition === 'pro' ? 'Pro' : 'Lite'}</span>
        </p>
      </div>
      {/* The same three as Cornerstone Jail and Risk, in the same order. */}
      <NavLink to="/account" className="account-menu__item" role="menuitem">
        <IconUser className="account-menu__icon" />
        My Profile
      </NavLink>
      <NavLink to="/preferences" className="account-menu__item" role="menuitem">
        <IconSettings className="account-menu__icon" />
        User Preferences
      </NavLink>
      <button type="button" className="account-menu__item account-menu__item--danger" role="menuitem" onClick={onSignOut}>
        <IconLogout className="account-menu__icon" />
        Sign Out
      </button>
    </div>
  )
}

/**
 * One person, drawn the same way everywhere.
 *
 * A photo is framed rather than cropped: the square frame clips, and the
 * image inside it carries the saved zoom and offset. The offsets are
 * percentages of the frame, so the framing a user adjusted at 240px is the
 * identical framing at 28px — which is the whole point of storing it.
 *
 * The signed link a photo arrives on is short-lived. An avatar that scrolls
 * into view on a page left open all morning therefore falls back to initials
 * on an image error rather than showing a browser's broken-image icon.
 */
export function Avatar({
  name,
  size = 36,
  src,
  transform,
  className,
}: {
  name: string
  size?: number
  src?: string | null
  transform?: PhotoTransform | null
  className?: string
}) {
  // Remembering WHICH link failed means a replacement photo gets its chance:
  // a new src is a new attempt, not a repeat of the one that broke.
  const [brokenSrc, setBrokenSrc] = useState<string | null>(null)
  const extra = className ? ` ${className}` : ''

  if (src && src !== brokenSrc) {
    return (
      <span className={`avatar avatar--photo${extra}`} style={{ width: size, height: size }} aria-hidden="true">
        <img
          className="avatar__img"
          src={src}
          alt=""
          style={{ transform: photoTransformCss(transform), transformOrigin: 'center' }}
          onError={() => setBrokenSrc(src)}
        />
      </span>
    )
  }

  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')

  return (
    <span className={`avatar${extra}`} style={{ width: size, height: size, fontSize: size * 0.38 }} aria-hidden="true">
      {initials || '?'}
    </span>
  )
}
